import { kebabCase } from 'lodash';
import React from 'react';
import { Icon, IconButton, Layout, Link, Typography } from '../../core';
import { i18n } from '../../i18n/translate';
import { formatClassName, pickDataAttributes } from '../../utils';
import { Button } from '../Button';
import { IconCategoryValueProps, LinkValue } from './types';
import { copyToClipboard } from './utils';
import { DesignSystem } from '@everfi/design-system';
import { CoreTooltipPlacement } from '../../core/components/Tooltip/types';

export const IconCategoryValue = <C,>(props: IconCategoryValueProps<C>) => {
  const {
    category,
    className,
    copyValue,
    dataAttributesForEdit = {},
    editAction,
    icon,
    iconProps,
    linkValues,
    tooltip,
    tooltipPlacement = CoreTooltipPlacement.RIGHT,
    type = 'inline',
    url,
    value,
  } = props;

  // The `.icon-category-value`/`.category-value` classes (component.legacy.tsx) drive the row's
  // margin-bottom and the label+value flex-wrap layout.
  const rootClassName = formatClassName(['icon-category-value', `${type}-icon-category-value`, className]);

  const dataAtrName = value ? `${kebabCase(String(value))}-icon-category-value` : 'icon-category-value';

  const dataAttributes = pickDataAttributes(props, dataAtrName);

  const renderLinkValues = () => {
    if (!linkValues) return;

    const getNewWindowProps = (link: LinkValue): object => {
      if (link?.newWindow) return { target: '_blank', rel: 'noopener noreferrer' };

      return {};
    };

    if (Array.isArray(linkValues)) {
      return linkValues.map((link, index) => {
        const newWindowProps = getNewWindowProps(link);
        const isLastLink = index === linkValues.length - 1;

        return (
          <>
            <a href={link.url} {...newWindowProps}>
              {link.label ? link.label : link.url} {link.newWindow && <Icon iconName="arrow-up-right-from-square" />}
            </a>

            {!isLastLink && ', '}
          </>
        );
      });
    }

    const newWindowProps = getNewWindowProps(linkValues);

    return (
      <a href={linkValues.url} {...newWindowProps}>
        {linkValues.label ? linkValues.label : linkValues.url}
      </a>
    );
  };

  return (
    <Layout
      {...dataAttributes}
      className={rootClassName}
      variant="flex"
      alignItems={'center'}
      columnGap={DesignSystem.Space['2XS']}
      rowGap={DesignSystem.Space.XS}
      css={{
        '& p': {
          mb: '0',
        },
        '& .value > a:not(.chakra-link)': { color: DesignSystem.Colors.TEXT_INTERACTIVE },
      }}
      textStyle="p2"
    >
      {/* @ts-ignore */}
      {icon && <Icon size="sm" iconName={icon} {...iconProps} />}
      <Layout className="category-value" variant="flex" gap={DesignSystem.Space.XS} flexWrap="wrap">
        {category && (
          <Typography
            as="span"
            fontWeight="semibold"
            css={{
              '&::after': {
                content: '":"',
              },
            }}
          >
            {category}
          </Typography>
        )}

        {type === 'stacked' && (
          // @ts-ignore
          <ButtonElements
            copyValue={copyValue}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            editAction={editAction}
            dataAttributesForEdit={dataAttributesForEdit}
            category={category}
            value={value}
          />
        )}

        <Layout className={formatClassName(['value', `${type}-value`])}>
          {url ? (
            <Link variant="neutral" href={url}>
              {value || url}
            </Link>
          ) : Array.isArray(value) ? (
            value.join(', ')
          ) : (
            value
          )}
          {linkValues && renderLinkValues()}

          {type === 'inline' && (
            // @ts-ignore
            <ButtonElements
              copyValue={copyValue}
              tooltip={tooltip}
              tooltipPlacement={tooltipPlacement}
              editAction={editAction}
              dataAttributesForEdit={dataAttributesForEdit}
              category={category}
              value={value}
            />
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

interface ButtonElementsProps<C> extends Pick<
  IconCategoryValueProps<C>,
  'value' | 'copyValue' | 'tooltip' | 'tooltipPlacement' | 'editAction' | 'category' | 'dataAttributesForEdit'
> {}

export const ButtonElements = <C,>(props: ButtonElementsProps<C>) => {
  const { copyValue, tooltip, tooltipPlacement, editAction, dataAttributesForEdit } = props;

  return (
    <>
      {copyValue && (
        <IconButton
          onClick={() => copyToClipboard(copyValue)}
          variant="link"
          paddingX={0}
          className="copy-button"
          aria-label={i18n.t('Copy')}
          icon={{ prefix: 'far', iconName: 'copy' }}
        />
      )}

      {tooltip && (
        <IconButton
          variant="link"
          colorScheme="info"
          className="info"
          icon={{ prefix: 'fas', iconName: 'info-circle' }}
          tooltip={{
            content: tooltip,
            placement: tooltipPlacement,
          }}
        />
      )}

      {editAction && (
        <Button
          {...dataAttributesForEdit}
          className="edit"
          style="link"
          icon="pen"
          label={i18n.t('Edit')}
          onClick={editAction}
          renderAsLegacy
        />
      )}
    </>
  );
};
